import { forwardRef, useState } from "react";
import { FieldError } from "react-hook-form";
import { DropdownOption } from "@mediaspace/shared/types";
import { FormFieldError } from "@mediaspace/shared/ui/form/FormElementStyles";
import { uniqueId } from "lodash";
import { translate } from "@mediaspace/shared/utils";
import { useUtilityClasses } from "./../fieldClasses";
import clsx from "clsx";
import {
    TruncatedContent,
    Content,
    Counter,
    CounterLine,
    Instruction,
    InterestButton,
    CounterAndMoreItemsLine
} from "./styled-components";
import SmartFormLabel from "../smart-form-label/SmartFormLabel";
import SmartContainer from "../smart-container/SmartContainer";
import { ShowMoreLessWrapperProps, TruncatedLine } from "@mediaspace/shared/ui";

export interface TagSelectionFieldProps {
    label: string;
    id?: string;
    options?: DropdownOption[];
    defaultValues?: string[];
    onChange: (values: string[]) => void;
    error: FieldError;
    required?: boolean;
    focusOnError?: boolean;
    maxSelection?: number;
    className?: string;
    instructionsText?: string;
}


interface InterestProps {
    // It's required for TruncateMarkup library to work properly that the DOM node contents are passed to the components as "children" prop.
    children: string;
    value: string;
    selected: boolean;
    disabled: boolean;
    onClick: (value: string) => void;
}


/**
 * individual interest item in the selection modal
 */
const Interest = forwardRef<HTMLButtonElement, InterestProps>((
    { children: label, value, selected, disabled, onClick },
    ref
) => {
    const handleClick = () => {
        onClick(value);
    };

    return (
        <InterestButton
            ref={ref}
            size={"small"}
            selected={selected}
            aria-pressed={selected}
            disabled={disabled && !selected}
            onClick={handleClick}
            value={label}
            className={"kms-ds-site-registration-selection-tag"}
        >
            <TruncatedLine>
                {label}
            </TruncatedLine>
        </InterestButton>
    );
});

/**
 * Tag Selection field for site siteRegistration form
 */
const TagSelectionField = forwardRef<HTMLButtonElement, TagSelectionFieldProps>(({
    label,
    id = uniqueId("input_"),
    options = [],
    defaultValues = [],
    onChange,
    error,
    required,
    maxSelection = 3,
    className,
    instructionsText,
}, ref) => {
    const [isTruncated, setIsTruncated] = useState(false);

    const [interests, setInterests] = useState(
        () => {
            const prepareInterests = (values: string[]) => {
                const optionValues = options.map((option) => {
                    return option.value;
                });
                const interests = Array.isArray(values)
                    ? values
                    : String(values)
                        .split(",")
                        .map((str) => {
                            return str.trim();
                        });
                return interests.filter((interest) =>
                    optionValues.includes(interest)
                );
            };

            return prepareInterests(defaultValues);
        });
    const interestsCount = interests.length;

    /**
     * generate Interest items
     */
    const generateInterests = (interests: DropdownOption[]) => {
        return interests.map((interest: DropdownOption, index: number) => (
            <Interest
                key={interest.value}
                value={interest.value}
                selected={interestSelected(interest.value)}
                disabled={interestsCount >= maxSelection}
                ref={index === 0 ? ref : undefined}
                onClick={toggleInterestSelection}
            >
                {interest.label}
            </Interest>
        ));
    };

    /**
     * is the current interest option selected
     * @param value
     */
    const interestSelected = (value: string) => {
        return interests.find((interest) => interest === value) !== undefined;
    };

    /**
     * toggle individual interest selection
     */
    const toggleInterestSelection = (value: string) => {
        let newInterests;

        if (interestSelected(value)) {
            newInterests = interests.filter((interest) => interest !== value);
        } else {
            newInterests = [...interests, value];
        }

        setInterests(newInterests);
        onChange(newInterests);
    };

    const classes = useUtilityClasses();

    const counter = <Counter className={classes.label} variant={"body2"}>
        {interestsCount}/{maxSelection}
    </Counter>;

    const ShowMoreLessWrapper = ({children}: ShowMoreLessWrapperProps) => <CounterAndMoreItemsLine>
        <div>{children}</div>
        <div>{counter}</div>
    </CounterAndMoreItemsLine>;

    return (
        <>
            <SmartContainer className={clsx(classes.root, className)}>
                <SmartFormLabel
                    htmlFor={id}
                    className={classes.label}
                    required={required}
                    text={label}
                />
            </SmartContainer>
            {!!instructionsText && (<Instruction variant={"body2"}>{instructionsText}</Instruction>)}
            <span className={classes.input}>
                <TruncatedContent
                    className={"kms-ds-site-registration-selection-modal-content"}
                    lines={2}
                    lineHeight={32}
                    showMoreInNewLine={true}
                    showMoreWrapper={ShowMoreLessWrapper}
                    showLessWrapper={ShowMoreLessWrapper}
                    onTruncate={setIsTruncated}
                >
                    {/* Add extra wrapper because TruncateMarkup library forces "display" style for the root elements in some cases, */}
                    {/* and we need the styled component's "display" to remain untouched */}
                    <div>
                        <Content>
                            {generateInterests(options)}
                        </Content>
                    </div>
                </TruncatedContent>
                {!isTruncated && <CounterLine>
                    {counter}
                </CounterLine>}
                <div aria-live="polite">
                    {error && (
                        <FormFieldError>
                            {translate("One or more selections are required")}
                        </FormFieldError>
                    )}
                </div>
            </span>
        </>
    );
});

export default TagSelectionField;
